import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import Config from '../../config/Config'
import { exchangeCodeForToken } from '../../service/auth-api'
import { toAppError } from '../../utils/ErrorHandler'
import LocalStorageUtils from '../../utils/LocalStorageUtils'
import {
  buildWebEamLoginUrl,
  getOAuthCodeFromQuery,
  generateAndSaveOAuthState,
  validateOAuthCallback,
} from '../../utils/LoginUtils'
import { isLocalDevEnvironment } from '../../utils/environment'
import { AppError, AppErrorCode } from '../../types/error.types'

//TODO: Consider adding qNumber to display
export type AuthState = {
  isLoggedIn: boolean
  accessToken?: string
  isLoading: boolean
  error?: AppError
}

const hasValidToken = LocalStorageUtils.isTokenValid()

const initialState: AuthState = {
  isLoggedIn: hasValidToken,
  accessToken: hasValidToken ? (LocalStorageUtils.getToken() ?? undefined) : undefined,
  isLoading: false,
}

export const startWebEamLogin = createAsyncThunk('auth/startWebEamLogin', async () => {
  const { state, nonce } = generateAndSaveOAuthState()
  const loginUrl = buildWebEamLoginUrl(state, nonce)
  window.location.href = loginUrl
})

export const handleOAuthRedirect = createAsyncThunk<
  { accessToken: string; expiresIn: number },
  { search: string },
  { rejectValue: AppError }
>('auth/handleOAuthRedirect', async ({ search }, { rejectWithValue }) => {
  const oauthCallbackValidation = validateOAuthCallback(search)
  const isLocalDevCallback = isLocalDevEnvironment()
  const devCode = isLocalDevCallback ? getOAuthCodeFromQuery(search) : undefined
  const code = oauthCallbackValidation.success ? oauthCallbackValidation.code : devCode

  if (!code) {
    const message = oauthCallbackValidation.success
      ? 'Missing code in callback'
      : oauthCallbackValidation.error

    return rejectWithValue({
      code: AppErrorCode.OAUTH_CALLBACK_INVALID,
      message,
    })
  }

  if (!oauthCallbackValidation.success && code) {
    console.warn('OAuth callback state validation failed, using local-dev callback fallback.')
  }

  try {
    const token = await exchangeCodeForToken({
      code,
      redirectUri: Config.WEB_EAM_REDIRECT_URI,
    })

    return {
      accessToken: token.access_token,
      expiresIn: token.expires_in,
    }
  } catch (error) {
    return rejectWithValue(
      toAppError(error, {
        fallbackCode: AppErrorCode.OAUTH_EXCHANGE_FAILED,
        fallbackMessage: 'Could not complete OAuth login.',
      }),
    )
  }
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.isLoggedIn = false
      state.accessToken = undefined
      state.error = undefined
      LocalStorageUtils.clearSession()
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(handleOAuthRedirect.pending, (state) => {
        state.isLoading = true
      })
      .addCase(
        handleOAuthRedirect.fulfilled,
        (state, action: PayloadAction<{ accessToken: string; expiresIn: number }>) => {
          const { accessToken, expiresIn } = action.payload
          state.isLoading = false
          state.isLoggedIn = true
          state.error = undefined
          state.accessToken = accessToken
          LocalStorageUtils.saveSession(accessToken, expiresIn)
        },
      )
      .addCase(
        handleOAuthRedirect.rejected,
        (state, action: PayloadAction<AppError | undefined>) => {
          state.isLoading = false
          state.isLoggedIn = false
          state.error = action.payload ?? {
            code: AppErrorCode.GENERIC_ERROR,
            message: 'Login failed',
          }
          state.accessToken = undefined
        },
      )
  },
})

export const { logout } = authSlice.actions
export const authReducer = authSlice.reducer
